import { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import api from '@/core/services/api'
import { usePopup } from '@/core/context/useWindow'
import GoldNewsModal from './GoldNewsModal'
import { G } from './theme'
import newsIcon from '@/assets/image/gold/news.png'

/**
 * 공지 띠 — gold8.bet: 둥근 어두운 띠 + 왼쪽 확성기 + 금색 글자가 오른쪽→왼쪽으로 흐름.
 *   데이터는 기존 WinNotice 와 같다(/site/popup 의 no 1 내용을 줄 단위로).
 *   $mobile: 모바일(m.gold8.bet) 크기.
 */
const SPEED = 80 // px/s

// 오른쪽 끝에서 들어와 왼쪽으로 완전히 빠져나갈 때까지 — Track 의 padding-left:100%(띠 폭)만큼
//   비운 채 시작하므로, 자기 폭(-100% = 띠 폭 + 글 폭)만큼 옮기면 마지막 글자가 왼쪽 끝을 지난다.
const flow = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`

const Root = styled.div`
  cursor: pointer;
  display: flex;
  /* 흐르는 글이 길어도 띠(그리고 페이지)가 늘어나지 않게 — flex 항목 최소 폭 0 */
  min-width: 0;
  max-width: 100%;
  align-items: center;
  height: ${({ $mobile }) => ($mobile ? '46px' : '66px')};
  padding: ${({ $mobile }) => ($mobile ? '0 12px 0 10px' : '0 20px 0 30px')};
  border-radius: ${({ $mobile }) => ($mobile ? '8px' : '33px')};
  background: ${({ $mobile }) => ($mobile ? G.mPanel : G.panel)};
  overflow: hidden;
`

/** 확성기 — gold 원본 news.png(120×120). PC 원본: 띠 폭의 3.3%(≈40px) × 1.2 */
const Icon = styled.img`
  flex: 0 0 auto;
  width: ${({ $mobile }) => ($mobile ? '34px' : '48px')};
  height: ${({ $mobile }) => ($mobile ? '34px' : '48px')};
  margin-right: ${({ $mobile }) => ($mobile ? '10px' : '30px')};
  object-fit: contain;
`

const Viewport = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`

const Track = styled.div`
  display: inline-block;
  padding-left: 100%;
  white-space: nowrap;
  animation: ${flow} ${({ $sec }) => $sec}s linear infinite;
`

const Line = styled.span`
  padding-right: 4em;
  font-size: ${({ $mobile }) => ($mobile ? '18px' : '36px')};
  color: ${G.goldText};
`

const toLines = (html) =>
  (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)

export default function GoldNews({ mobile = false }) {
  const [lines, setLines] = useState([])
  const [sec, setSec] = useState(40)
  const trackRef = useRef(null)
  const { openPopup } = usePopup()

  // 띠를 누르면 「最新ニュース」 창 — 흐르는 줄 전체를 목록으로
  const openNews = () => {
    if (!lines.length) return
    openPopup({ raw: true, content: <GoldNewsModal lines={lines} mobile={mobile} /> })
  }

  useEffect(() => {
    api.get('/site/popup')
      .then((res) => {
        const item = (res.data?.content?.data || []).find(d => d.no === 1)
        // 관리자 공지(/site/popup no 1)만 흐른다 — 없으면 확성기만 있는 빈 띠
        setLines(toLines(item?.contents))
      })
      .catch(console.error)
  }, [])

  // (띠 폭 + 글 폭) ÷ 속도 = 한 바퀴 시간 — 글 길이와 상관없이 같은 속도로 흐르게
  useEffect(() => {
    if (trackRef.current) setSec(Math.max(8, Math.round(trackRef.current.offsetWidth / SPEED)))
  }, [lines])

  return (
    <Root
      $mobile={mobile}
      role="button"
      tabIndex={0}
      onClick={openNews}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openNews() } }}
    >
      <Icon $mobile={mobile} src={newsIcon} alt="" />
      <Viewport>
        {lines.length > 0 && (
          <Track ref={trackRef} $sec={sec}>
            {lines.map((l, i) => <Line key={i} $mobile={mobile}>{l}</Line>)}
          </Track>
        )}
      </Viewport>
    </Root>
  )
}
